/**
 * Import Status Drawer
 * Floating panel for tracking bulk import jobs in the background.
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, ChevronUp, ChevronDown, X, Loader2, CheckCircle2,
  AlertTriangle, Clock, ExternalLink, RotateCcw, Trash2
} from 'lucide-react';
import { useBulkImport } from '../../contexts/BulkImportContext';
import { formatTimeAgoIST, formatISTDate } from '../../utils/timezone';

export const ImportStatusDrawer = () => {
  const {
    jobs,
    activeJobCount,
    isDrawerExpanded,
    setIsDrawerExpanded,
    openJob,
    cancelJob,
    deleteJob,
  } = useBulkImport();
  
  // Don't render if no jobs
  if (jobs.length === 0) return null;
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'classifying':
      case 'creating':
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'review':
        return <Clock className="w-4 h-4 text-amber-400" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
      case 'failed':
      case 'cancelled':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'classifying':
      case 'creating':
        return 'bg-blue-500';
      case 'review':
        return 'bg-amber-500';
      case 'completed':
        return 'bg-emerald-500';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-slate-500';
    }
  };
  
  const getStatusText = (job) => {
    switch (job.status) {
      case 'pending':
        return 'Pending';
      case 'mapping':
        return 'Mapping columns';
      case 'classifying':
        return `AI classifying... ${job.classified_count}/${job.total_parts}`;
      case 'review':
        return 'Ready for review';
      case 'creating':
        return `Creating parts... ${job.created_count}/${job.total_parts}`;
      case 'completed':
        return `Completed • ${job.created_count} parts created`;
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return job.status;
    }
  };
  
  const formatTime = (isoString) => {
    if (!isoString) return '';
    return formatTimeAgoIST(isoString) || formatISTDate(isoString);
  };
  
  // Handle click on collapsed drawer - open modal for active job
  const handleHeaderClick = () => {
    if (!isDrawerExpanded) {
      // If collapsed and there's an active job, open the modal directly
      const activeJob = jobs.find(j => j.status === 'classifying' || j.status === 'creating' || j.status === 'review');
      if (activeJob) {
        openJob(activeJob.job_id);
        return;
      }
    }
    setIsDrawerExpanded(!isDrawerExpanded);
  };
  
  return (
    <div className="fixed bottom-0 right-4 z-40" data-testid="import-status-drawer">
      <motion.div
        initial={false}
        animate={{ 
          height: isDrawerExpanded ? 'auto' : 48,
          width: isDrawerExpanded ? 384 : 288 // 96*4=384 (w-96), 72*4=288 (w-72)
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="bg-slate-900 border border-slate-700 border-b-0 rounded-t-xl shadow-2xl overflow-hidden"
      >
        {/* Header - Always visible */}
        <div
          onClick={handleHeaderClick}
          className="px-4 py-3 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between cursor-pointer hover:bg-slate-800 transition-colors"
          style={{ height: 48 }}
          data-testid="import-status-header"
        >
          {isDrawerExpanded ? (
            <>
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-400" />
                <span className="font-medium text-white">Import Status</span>
                {activeJobCount > 0 && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                    {activeJobCount} active
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                {activeJobCount > 0 ? (
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
                <span className="text-sm text-white font-medium">
                  {activeJobCount > 0 
                    ? `${activeJobCount} import${activeJobCount > 1 ? 's' : ''} in progress - Click to view`
                    : `${jobs.length} recent import${jobs.length > 1 ? 's' : ''}`
                  }
                </span>
              </div>
              <ChevronUp className="w-4 h-4 text-slate-400" />
            </>
          )}
        </div>
        
        {/* Jobs list - Only visible when expanded */}
        <AnimatePresence>
          {isDrawerExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="max-h-96 overflow-y-auto"
            >
              {jobs.map(job => (
                <div
                  key={job.job_id}
                  className="p-4 border-b border-slate-800 hover:bg-slate-800/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(job.status)} ${
                        job.status === 'classifying' || job.status === 'creating' ? 'animate-pulse' : ''
                      }`} />
                      <span className="text-sm font-medium text-white truncate max-w-[180px]">
                        {job.name || 'Bulk Import'}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 flex-shrink-0">
                      {formatTime(job.created_at)}
                    </span>
                  </div>
                  
                  <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5">
                    {getStatusIcon(job.status)}
                    {getStatusText(job)}
                  </p>
                  
                  {/* Progress bar for active jobs */}
                  {(job.status === 'classifying' || job.status === 'creating') && (
                    <div className="mb-3">
                      <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-blue-500 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${job.progress_percent}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-slate-500">
                        <span>{job.progress_percent}%</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Stats for completed jobs */}
                  {job.status === 'completed' && (
                    <div className="flex items-center gap-3 text-xs mb-3">
                      <span className="text-emerald-400">{job.created_count} created</span>
                      {job.duplicate_count > 0 && (
                        <span className="text-slate-500">{job.duplicate_count} duplicates</span>
                      )}
                      {job.error_count > 0 && (
                        <span className="text-red-400">{job.error_count} errors</span>
                      )}
                    </div>
                  )}
                  
                  {/* Action buttons */}
                  <div className="flex gap-2">
                    {job.status === 'review' && (
                      <button
                        onClick={() => openJob(job.job_id)}
                        className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition-colors"
                      >
                        Review & Create
                      </button>
                    )}
                    
                    {(job.status === 'classifying' || job.status === 'creating') && (
                      <>
                        <button
                          onClick={() => openJob(job.job_id)}
                          className="flex-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs transition-colors"
                        >
                          Open
                        </button>
                        <button
                          onClick={() => cancelJob(job.job_id)}
                          className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-xs transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    
                    {job.status === 'completed' && (
                      <>
                        <button
                          onClick={() => openJob(job.job_id)}
                          className="flex-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs transition-colors"
                        >
                          View Results
                        </button>
                        <button
                          onClick={() => deleteJob(job.job_id)}
                          className="px-3 py-1.5 hover:bg-slate-700 text-slate-400 rounded text-xs transition-colors"
                          title="Dismiss"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    
                    {(job.status === 'failed' || job.status === 'cancelled') && (
                      <>
                        <button
                          onClick={() => openJob(job.job_id)}
                          className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition-colors"
                        >
                          <RotateCcw className="w-3 h-3 inline mr-1" />
                          Retry
                        </button>
                        <button
                          onClick={() => deleteJob(job.job_id)}
                          className="px-3 py-1.5 hover:bg-slate-700 text-slate-400 rounded text-xs transition-colors"
                          title="Dismiss"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              
              {jobs.length === 0 && (
                <div className="p-6 text-center text-slate-500 text-sm">
                  No import jobs
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default ImportStatusDrawer;
